/**
 * The URL + memory glue for the dashboards' shared filters (D2 plan R19, spec
 * §4.1), used by /overview and /channels. Reads happen during render, so the
 * first render already has the right period. Writes REPLACE the history entry
 * and never push: Back leaves the page instead of stepping through filters.
 *
 * - Whenever the URL does not hold the shared filters in force, it is
 *   rewritten with them: on mount, after the menu link empties it, after a
 *   hand edit. The check is by value, so a URL already holding them is never
 *   rewritten just to reorder its keys.
 * - The memory follows the shared filters in force. D2's own keys are never
 *   remembered.
 * - The memory is read again whenever the URL changes, so an emptied URL gets
 *   the latest values, not the ones read on mount.
 *
 * One write per event: react-router's functional setter builds on the search
 * params of the last render, so two writes in one tick keep only the second.
 *
 * @module hooks/useDashboardFilters
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bangkokToday } from '@/services/looker/lookerDateRange';
import {
  parseChannelUrl, parseShared, readFilterMemory, sharedSearchOf, writeChannelUrl, writeFilterMemory, writeShared,
  type ChannelUrlFilters, type SharedFilters,
} from '@/services/dashboardFilters';

interface DashboardFilters {
  shared: SharedFilters;
  setShared: (patch: Partial<SharedFilters>) => void;
  channel: ChannelUrlFilters;
  setChannel: (patch: Partial<ChannelUrlFilters>) => void;
  /** period=…&objective=…[&currency=…]: what a link to the other dashboard carries. */
  sharedSearch: string;
}

const REPLACE = { replace: true } as const;

/** The URL already holds exactly the values in force (no currency key = none picked). */
const carries = (params: URLSearchParams, f: SharedFilters): boolean =>
  params.get('period') === f.period && params.get('objective') === f.objective && params.get('currency') === f.currency;

export const useDashboardFilters = (): DashboardFilters => {
  const [params, setParams] = useSearchParams();
  const search = params.toString();
  const today = bangkokToday();
  const shared = useMemo(() => parseShared(new URLSearchParams(search), readFilterMemory(), today), [search, today]);
  const channel = useMemo(() => parseChannelUrl(new URLSearchParams(search)), [search]);
  const { period, objective, currency } = shared;

  useEffect(() => {
    const inForce = { period, objective, currency };
    const current = new URLSearchParams(search);
    if (!carries(current, inForce)) setParams(writeShared(current, inForce), REPLACE);
  }, [search, period, objective, currency, setParams]);

  useEffect(() => {
    writeFilterMemory({ period, objective, currency });
  }, [period, objective, currency]);

  const setShared = useCallback((patch: Partial<SharedFilters>) => {
    setParams((prev) => writeShared(prev, { period, objective, currency, ...patch }), REPLACE);
  }, [setParams, period, objective, currency]);

  const setChannel = useCallback((patch: Partial<ChannelUrlFilters>) => {
    setParams((prev) => writeChannelUrl(prev, { ...channel, ...patch }), REPLACE);
  }, [setParams, channel]);

  const sharedSearch = useMemo(() => sharedSearchOf({ period, objective, currency }), [period, objective, currency]);

  return useMemo(
    () => ({ shared, setShared, channel, setChannel, sharedSearch }),
    [shared, setShared, channel, setChannel, sharedSearch],
  );
};
